import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ShieldCheck, Mail, Facebook, MessageSquare } from 'lucide-react';
import Reveal from './Reveal';
import { PHONE_DISPLAY, PHONE_TEL, ABN, INSURANCE, EMAIL, EMAIL_LINK, FACEBOOK_URL, CARPENTER_QUALIFICATION_FOOTER, CARPENTER_QUALIFICATION_FULL, CTA_SECONDARY_LABEL, REVIEW_RATING, REVIEW_COUNT, GOOGLE_REVIEWS_URL } from '../constants';
import { BrandLockup } from './Brand';
import QuoteForm from './QuoteForm';
import { trackEvent } from '../analytics';
import { SERVICE_SUBURBS } from './ServiceArea';
import { SERVICES } from '../servicesData';
import { useEnquirySmsHref } from '../lib/enquiry';

// Footer background — chose the "cladding-contemporary-luxury-facade"
// portfolio shot (also used as the decks-005ba9 catalogue card). 60%+ of
// the frame is warm hardwood deck boards with raking golden sunlight —
// texture over scenery, exactly what a heavily-overlaid footer background
// wants. Sharp at 1600w, sits under a ~80–96% dark overlay so every
// contact line (including the ABN) holds ≥4.5:1 contrast.
const PUB = process.env.PUBLIC_URL || '';
const footerBgWebp1600 = `${PUB}/portfolio/cladding-contemporary-luxury-facade-1600.webp`;
const footerBgJpg1600 = `${PUB}/portfolio/cladding-contemporary-luxury-facade-1600.jpg`;
const footerBgWebp800 = `${PUB}/portfolio/cladding-contemporary-luxury-facade-800.webp`;
const footerBgJpg800 = `${PUB}/portfolio/cladding-contemporary-luxury-facade-800.jpg`;

const Footer = ({ editorial = false }) => {
  const smsHref = useEnquirySmsHref();
  return (
    <footer className="site-footer" data-testid="site-footer">
      {/* Deck-boards + raking light background. Anchored bottom so the
          board perspective leads the eye down. Lazy-loaded (below fold),
          explicit dims prevent CLS. Purely atmospheric — every text line
          sits on a heavy dark overlay. */}
      <picture className="site-footer-bg" aria-hidden="true">
        <source type="image/webp" media="(max-width: 768px)" srcSet={footerBgWebp800} />
        <source media="(max-width: 768px)" srcSet={footerBgJpg800} />
        <source type="image/webp" srcSet={footerBgWebp1600} />
        <img
          src={footerBgJpg1600}
          width="1600"
          height="2384"
          loading="lazy"
          decoding="async"
          alt=""
          className="site-footer-bg-img"
        />
      </picture>
      <div className="site-footer-overlay" aria-hidden="true" />

      <div className="container">
        <Reveal as="div" className="footer-cta" id="contact">
          <p className="footer-tagline">{editorial ? 'Let’s talk / Hobart, Australia' : 'Quality Work. Fair Price. Built to Last.'}</p>
          <h2 className="footer-cta-title">{editorial ? <>Something in mind?<br /><span className="h-accent">Let&rsquo;s start there.</span></> : <>Quick fix or full build &mdash; <span className="h-accent">ready when you are.</span></>}</h2>
          <p className="footer-cta-text">
            Call, text, or send the details &mdash; I&rsquo;ll get back to you.
          </p>

          {/* Call is the single primary action; Text is the secondary outline
              so the two CTAs keep a clear visual hierarchy. Both fire GA4
              events; call_click + sms_click also map to Meta "Contact". */}
          <div className="footer-contact-actions" data-testid="footer-contact-actions">
            <a
              href={PHONE_TEL}
              className="btn btn-primary footer-contact-btn"
              data-testid="footer-call-btn"
              aria-label={`${CTA_SECONDARY_LABEL} — ${PHONE_DISPLAY}`}
              onClick={() => trackEvent('call_click', { location: 'footer' })}
            >
              <Phone size={22} strokeWidth={2.2} aria-hidden="true" />
              <span>{CTA_SECONDARY_LABEL}</span>
            </a>
            <a
              href={smsHref}
              className="btn btn-secondary btn-secondary-on-dark footer-contact-btn"
              data-testid="footer-sms-btn"
              aria-label={`Text Corey — ${PHONE_DISPLAY}`}
              onClick={() => trackEvent('sms_click', { location: 'footer' })}
            >
              <MessageSquare size={22} strokeWidth={2.2} aria-hidden="true" />
              <span>Text Corey</span>
            </a>
          </div>

          <QuoteForm />
        </Reveal>

        <div className="footer-meta">
          <div className="footer-meta-brand">
            <BrandLockup variant="dark" />
            <span className="site-header-constr footer-constr" aria-hidden="true">CONSTRUCTION</span>
            <p className="footer-triad" aria-hidden="true">Carpentry &middot; Creation &middot; Restoration</p>
          </div>

          {/* Services column — real routes (not anchors). Same list drives
              the NavDrawer hamburger + the sitemap; source of truth is
              servicesData.SERVICES. Ensures every service page has a
              sitewide referrer, closes the Home → service page conversion
              gap for the Google Ads suburbs group. */}
          <div className="footer-meta-services" data-testid="footer-services-column">
            <p className="footer-column-label">Services</p>
            <ul className="footer-services-list">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={s.path}
                    className="footer-services-link"
                    data-testid={`footer-service-link-${s.slug}`}
                    onClick={() => trackEvent('footer_service_click', { location: s.slug })}
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-meta-info">
            <p className="footer-info-line">
              <MapPin size={18} strokeWidth={2} aria-hidden="true" />
              <span>Hobart and surrounds, Tasmania, Australia</span>
            </p>
            <p className="footer-info-line">
              <Phone size={18} strokeWidth={2} aria-hidden="true" />
              <a
                href={PHONE_TEL}
                className="footer-info-phone"
                onClick={() => trackEvent('call_click', { location: 'footer_info' })}
              >{PHONE_DISPLAY}</a>
            </p>
            <p className="footer-info-line">
              <Mail size={18} strokeWidth={2} aria-hidden="true" />
              <a href={EMAIL_LINK} className="footer-info-email" data-testid="footer-email-link">{EMAIL}</a>
            </p>
            <p className="footer-info-line footer-credentials" data-testid="footer-credentials">
              <ShieldCheck size={18} strokeWidth={2} aria-hidden="true" />
              <span>{CARPENTER_QUALIFICATION_FOOTER} · {INSURANCE} · ABN {ABN}</span>
            </p>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="FairFix on Facebook"
              data-testid="footer-facebook-link"
              onClick={() => trackEvent('facebook_click', { location: 'footer' })}
            >
              <Facebook size={22} strokeWidth={2} aria-hidden="true" />
            </a>
          </div>
        </div>

        <p className="footer-copyright">{`© ${new Date().getFullYear()} FairFix. All rights reserved.`}</p>
        <p className="footer-legal" data-testid="footer-legal-line">
          {editorial ? `FairFix Construction · ABN ${ABN}` : `FairFix Construction (ASIC Registered Business Name) · ABN ${ABN}`}
        </p>
        <p className="footer-pay-row">
          <Link to="/pay" className="footer-services-link footer-pay-link" data-testid="footer-pay-link"
            onClick={() => trackEvent('pay_link_click', { location: 'footer' })}>
            Pay an invoice
          </Link>
        </p>
        <p className="footer-reviews-line">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="footer-reviews-link"
            data-testid="footer-reviews-link"
            onClick={() => trackEvent('google_reviews_click', { location: 'footer' })}
          >
            {editorial ? 'Read customer reviews on Google' : `${REVIEW_RATING} from ${REVIEW_COUNT} Google reviews`}
          </a>
        </p>
      </div>

      {/* LocalBusiness structured data for local SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'HomeAndConstructionBusiness',
            '@id': 'https://fairfix.com.au',
            url: 'https://fairfix.com.au',
            name: 'FairFix Construction',
            legalName: 'FairFix Construction',
            description:
              'Qualified carpenter and residential construction specialist in Hobart. Certificate III in Carpentry, 20 years on the tools. Extensions, timber framing, decks, cladding, custom carpentry and property repairs. Fair, upfront pricing.',
            // Derive the E.164 phone from the shared PHONE_TEL constant so
            // there is a single source of truth for the number. Stripping
            // the "tel:" prefix leaves the schema.org-required E.164 form
            // (e.g. "+61434727099"). If PHONE_TEL is ever updated, this
            // schema value updates with it automatically — preventing the
            // class of bug where a duplicate literal is missed on a
            // sitewide phone swap.
            telephone: PHONE_TEL.replace(/^tel:/, ''),
            email: EMAIL,
            sameAs: [FACEBOOK_URL],
            identifier: {
              '@type': 'PropertyValue',
              propertyID: 'ABN',
              value: '95 386 997 041',
            },
            // Owner's trade qualification (Certificate III in Carpentry) —
            // encoded as an EducationalOccupationalCredential per schema.org.
            hasCredential: {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Trade Qualification',
              name: CARPENTER_QUALIFICATION_FULL,
              educationalLevel: 'Certificate III',
              recognizedBy: {
                '@type': 'Organization',
                name: 'Australian Qualifications Framework',
              },
            },
            areaServed: SERVICE_SUBURBS.map((name) => ({ '@type': 'City', name })),
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Hobart',
              addressRegion: 'TAS',
              addressCountry: 'AU',
            },
            geo: { '@type': 'GeoCoordinates', latitude: -42.8821, longitude: 147.3272 },
            priceRange: '$$',
          }),
        }}
      />
    </footer>
  );
};

export default Footer;
